import React, { useEffect, useState } from 'react';
import { useRecoilState, useResetRecoilState } from 'recoil';
import { mergeDeepRight, omit, pick, uniq } from 'ramda';
import NewSaveBar from '~/components/organism/NewSaveBar';
import JustificationContainer from '~/components/atom/JustificationContainer';
import useViewingModeProps from '~/hooks/useViewingModeProps';
import { UserRole, type GetAccountQuery } from '~/graphql/types';
import {
  brandSettingsCurrent,
  brandSettingsInitial,
  defaultColors,
  type BrandSettingsState,
} from '../../state';
import getDifferenceCount from '../../utils/getDifferenceCount';
import useUpdateMutations from '../../hooks/useUpdateMutations';
import getValidationMessages from '../../utils/getValidationMessages';
import AccountBlock, { type ImageUrls } from '../AccountBlock';
import type { SaveBarMessage } from '~/components/organism/NewSaveBar/components/MessagesContainer';
import usePermissions from '~/hooks/usePermissions';
import EmailSettingsBlock from '../EmailSettingsBlock';
import type { DeepPartial } from 'utility-types';
import removeTypenames from '~/util/removeTypenames';

export type Props = {
  accountData: GetAccountQuery | undefined;
};

const text = {
  notAuthorized: 'Alleen accountbeheerders kunnen wijzigingen aanbrengen.',
};

const nonAdminMessage: SaveBarMessage = {
  key: 'notAuthorized',
  message: text.notAuthorized,
  type: 'warning',
};

const BrandBlocks: React.FCC<Props> = ({ accountData }) => {
  const hasFlows = usePermissions(['root.automation']).allowed;
  const viewingModeProps = useViewingModeProps();
  const [initialChanges, setInitialChanges] =
    useRecoilState(brandSettingsInitial);
  const [currentChanges, setCurrentChanges] =
    useRecoilState(brandSettingsCurrent);

  const resetInitial = useResetRecoilState(brandSettingsInitial);
  const resetCurrent = useResetRecoilState(brandSettingsCurrent);

  const initialImageUrls: ImageUrls = {
    logo: accountData?.getAccount?.AccountSettings?.logo?.url,
    teamImage: accountData?.getAccount?.AccountSettings?.teamImage?.url,
    backgroundImage:
      accountData?.getAccount?.AccountSettings?.backgroundImage?.url,
  };
  const [imageUrls, setImageUrls] = useState<ImageUrls>(initialImageUrls);

  const mayEdit = accountData?.getAccount?.relation.role === UserRole.Admin;
  const accountSettings = accountData?.getAccount?.AccountSettings;

  const initialValues: BrandSettingsState = {
    unsubscribeLinkText: null,
    logo: accountSettings?.logo
      ? {
          s3key: accountSettings.logo.s3key,
        }
      : null,
    teamImage: accountSettings?.teamImage
      ? {
          s3key: accountSettings.teamImage.s3key,
        }
      : null,
    backgroundImage: accountSettings?.backgroundImage
      ? {
          s3key: accountSettings.backgroundImage.s3key,
        }
      : null,
    colors: accountSettings?.colors
      ? removeTypenames(accountSettings.colors)
      : defaultColors,
    url: accountSettings?.url || null,
    style: {
      fontFamily: accountSettings?.style?.fontFamily || null,
    },
  };

  useEffect(() => {
    setInitialChanges(initialValues);
    setCurrentChanges(initialValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () => () => {
      resetInitial();
      resetCurrent();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const { changedPaths, differenceCount } = getDifferenceCount({
    initial: initialChanges,
    updated: currentChanges,
  });

  const uniqueTopLevelKeys = uniq(
    changedPaths.map(arr => arr[0] as keyof BrandSettingsState),
  );

  const { onUpdate, loading: updateLoading } = useUpdateMutations({
    flowsAllowed: hasFlows,
    changedFields: pick(uniqueTopLevelKeys, currentChanges),
    prevData: accountData?.getAccount,
  });

  const onSave = async () =>
    await onUpdate({
      onReset: () => setInitialChanges(currentChanges),
    });

  const onCancel = () => {
    setImageUrls(initialImageUrls);
    setCurrentChanges(initialChanges);
  };

  const disabled = !mayEdit || updateLoading || differenceCount === 0;
  const messages =
    mayEdit === false
      ? [nonAdminMessage]
      : differenceCount !== 0
        ? getValidationMessages({
            changes: currentChanges,
            flowsAllowed: hasFlows,
          })
        : undefined;

  const onUpdateField = (update: DeepPartial<BrandSettingsState>) => {
    setCurrentChanges(
      prev => mergeDeepRight(prev, update) as BrandSettingsState,
    );
  };

  return (
    <JustificationContainer direction="column" gap="xl">
      <NewSaveBar
        onCancel={onCancel}
        onSave={onSave}
        messages={messages}
        loading={updateLoading}
        disabled={disabled}
        changes={differenceCount}
      />
      <AccountBlock
        values={omit(['unsubscribeLinkText'], currentChanges)}
        readOnly={!mayEdit}
        imageUrls={imageUrls}
        onUpdate={onUpdateField}
        onImageUpdate={({ fieldName, value }) => {
          setImageUrls(prev => ({
            ...prev,
            [fieldName]: value?.url,
          }));
          onUpdateField({ [fieldName]: value ? { s3key: value.s3key } : null });
        }}
        {...viewingModeProps}
      />
      <EmailSettingsBlock
        onUpdate={onUpdateField}
        readOnly={!mayEdit}
        fontFamily={currentChanges.style?.fontFamily || null}
        hasFlows={hasFlows}
      />
    </JustificationContainer>
  );
};

export default BrandBlocks;
